import { observer } from "mobx-react-lite";
import { FunctionComponent } from "react";

import { CenteredLargeSpinner, Spinner } from "@bps/fluent-ui";
import { AppInsightsEventNames } from "@libs/analytics/app-insights/app-insights.enums.ts";
import { useAppTrackEvent } from "@libs/analytics/app-insights/useAppTrackEvent.tsx";
import { routes } from "@libs/routing/routes.ts";
import { PatientRecordScreenContext } from "@modules/clinical/screens/context/PatientRecordScreenContext.ts";
import { PatientRecordScreenHelper } from "@modules/clinical/screens/context/PatientRecordScreenHelper.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import {
  DataFetcher,
  withFetch
} from "@ui-components/data-fetcher/DataFetcher.tsx";

import { PatientRecordScreenLoader } from "../context/PatientRecordScreenLoader.ts";
import { PatientRecordDetails } from "./components/PatientRecordDetails.tsx";

const PatientRecordScreenBase: FunctionComponent = observer(() => {
  const root = useStores();
  const { routing } = root;

  const patientId = routing.match(routes.records.record, true)?.params.id;
  const encounterId = routing.match(routes.records.encounter, true)?.params
    .encounterId;

  const calendarEventId = routing.match(routes.records.appointment)?.params
    .calendarEventId;

  const isView = !!routing.match(routes.records.recordView);

  const trackEvent = useAppTrackEvent(AppInsightsEventNames.encounterStarted);

  if (!patientId) {
    return null;
  }

  return (
    <DataFetcher
      refetchId={`${patientId}${encounterId}${isView}`}
      fetch={async () => {
        const loader = new PatientRecordScreenLoader(root, {
          patientId,
          encounterId,
          calendarEventId
        });

        const { clinicalRecord, showEncounterExistsPrompt } =
          await loader.loadClinicalRecord();

        const helper = new PatientRecordScreenHelper(
          root,
          clinicalRecord,
          showEncounterExistsPrompt
        );
        await helper.loadHelper(trackEvent);
        return helper;
      }}
      fallback={<CenteredLargeSpinner />}
    >
      {helper => (
        <PatientRecordScreenContext.Provider value={helper}>
          <PatientRecordDetails />
        </PatientRecordScreenContext.Provider>
      )}
    </DataFetcher>
  );
});

export const PatientRecordScreen = withFetch(
  x => [
    x.clinical.ref.clinicalNoteSections.load(),
    x.billing.getInvoiceSettings()
  ],
  PatientRecordScreenBase,
  { fallback: <Spinner /> }
);

// ⚠ It should be exported as default since it is used for React.lazy
// eslint-disable-next-line import/no-default-export
export default PatientRecordScreen;
